import type { AxiosHeaders } from 'axios';
import { api } from './index';
import { type ChargeMovement } from './charge-movements';

const BASE_PATH = '/api/internal/charges';

export interface ShiftSummary {
  shiftsCount: number;
  price: number;
  totalPrice: number;
}
export interface ChargeSummary {
  career: string;
  shiftType: string;
  shiftsCount: number;
  price: number;
  shiftsSummary: Array<ShiftSummary>;
}

export interface ShiftsUniqueDates {
  [key: string]: string;
  all: string;
}

/**
 * How far the period's tax document got: with the provider ('emitting'), taken by
 * the authority ('emitted'), taken but with turnos or adjustments added since
 * that are still to bill ('partial'), refused or never sent ('failed'), frozen
 * by a closure that never got a DTE ('unemitted'), or nothing to show — no
 * closure, or one annulled through a credit note.
 */
export type ChargeTaxDocumentStatus = 'emitting' | 'emitted' | 'partial' | 'failed' | 'unemitted' | null;

export type Charge = {
  patient: {
    id: number;
    fullName: string;
    representative: {
      id: number;
      firstName: string;
      fullName: string;
      email: string;
      phoneNumber: string;
    } | null
    // Who the charges email reaches: the representatives chosen for billing emails
    // that have an email (the first representative while none is chosen).
    billingRecipients: Array<{ fullName: string; email: string }>;
  };
  price: number;
  shiftsCount: number;
  checkedInShiftsCount: number;
  chargesSummary: Array<ChargeSummary>;
  lastEmailSentAt: string;
  chargeMovements: Array<ChargeMovement>;
  totalChargeMovements: number;
  amountPendingToCharge: number;
  shiftsUniqueDates: ShiftsUniqueDates;
  taxDocumentStatus: ChargeTaxDocumentStatus;
}
export interface PDFCharge {
  headers: AxiosHeaders;
  data: Blob;
}

export interface XlsxCharges {
  headers: AxiosHeaders;
  data: Blob;
}

export const chargesApi = {
  index(queryParams: string): Promise<Array<Charge>> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?${queryParams}`,
    }).then(response => response.data.charges);
  },
  indexAsXlsx(queryParams: string): Promise<XlsxCharges> {
    return api({
      method: 'get',
      url: `${BASE_PATH}.xlsx?${queryParams}`,
      responseType: 'blob',
    });
  },
  show(id: number, queryParams: string, format: string): Promise<PDFCharge> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}.${format}?${queryParams}`,
      responseType: format === 'pdf' ? 'blob' : 'json',
    });
  },
  showRepresentativePdf(
    representativeId: number, queryParams: string, withoutAmounts = false,
  ): Promise<PDFCharge> {
    const params = [queryParams, withoutAmounts ? 'without_amounts=true' : '']
      .filter(Boolean).join('&');

    return api({
      method: 'get',
      url: `/api/internal/representative_charges/${representativeId}.pdf?${params}`,
      responseType: 'blob',
    });
  },
};
